<script setup>
import { onMounted, ref } from 'vue'
import { userCollection, destroyUser } from '@/services/user'
import { useRouter } from 'vue-router';
import { humanizeText, swalError, swalSuccess } from '@/helper';

const users = ref({
  filter_option: {},
  list: []
})
const roleMap = ref({}) // Map role names to IDs
const availableRoles = ref([]) // List of actual roles from database
const isActive = ref('all')
onMounted(async ()=>{
  let response = await userCollection()
  if (response.error){
    swalError(response.result, true)
    return
  }
  users.value.list = processUserCollection(response)
  users.value.filter_option = response.data.data.filter_option
  
  // Build role map and available roles from included data
  if (response.data.data.users && response.data.data.users.included) {
    const roles = []
    response.data.data.users.included.forEach(item => {
      if (item.type === 'role') {
        const roleKey = item.attributes.name.toLowerCase().replace(/ /g, '_')
        roleMap.value[roleKey] = item.id
        
        // Only add role if it has users (count > 0)
        const count = users.value.filter_option[roleKey] || 0
        if (count > 0) {
          roles.push({
            key: roleKey,
            name: item.attributes.name,
            id: item.id,
            count: count
          })
        }
      }
    })
    availableRoles.value = roles
  }
})

function processUserCollection(response){
  if(!response.data.data || response.data.data.empty){
    return []
  }
  let list = response.data.data.users.data.map( userObj => {
    return {
      id: userObj.attributes.id,
      email: userObj.attributes.email,
      name: `${userObj.attributes.first_name} ${userObj.attributes.last_name}`,
      role: getRoleName(userObj, response.data.data.users.included)
    }
  })
  return list
}

function getRoleName(userObj, included) {
  if (!userObj.relationships || !userObj.relationships.role || !userObj.relationships.role.data) return 'No Role Assigned';
  const roleId = userObj.relationships.role.data.id;
  const role = included.find(inc => inc.type === 'role' && inc.id === roleId);
  return role ? role.attributes.name : 'No Role Assigned';
}

const searchInput = ref()
async function searchUser(){
  let response = await userCollection({search: searchInput.value})
  if (response.error){
    swalError(response.result, true)
    return
  }
  users.value.list = processUserCollection(response)
  users.value.filter_option = response.data.data.filter_option
}

const isLoading = ref(false)
async function filterItem(item){
  isActive.value = item
  
  // If 'all', fetch all users
  if (item === 'all') {
    let response = await userCollection()
    if (response.error){
      swalError(response.result, true)
      return
    }
    users.value.list = processUserCollection(response)
    return
  }
  
  // Otherwise, filter by role_id
  const roleId = roleMap.value[item]
  if (!roleId) {
    console.error('Role ID not found for:', item)
    return
  }
  
  let response = await userCollection({role_id: roleId})
  if (response.error){
    swalError(response.result, true)
    return
  }
  users.value.list = processUserCollection(response)
}

async function deleteUser(id, ind){
  let response = await destroyUser(id)
  if (response.error){
    swalError(response.result, true)
    return
  }
  users.value.list.splice(ind, 1)
  swalSuccess('User successfully deleted')
}

const router = useRouter()
function viewUser(id){
  router.push({name: 'user',params: {id}})
}
</script>

<template>
  <Breadcrumbs main="Users"/>
  <div class="container-fluid text-start">
    <div class="row">
      <!-- Search Input -->
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <router-link :to="{name: 'add-user'}"
            class="btn btn-light-primary"
          >
            Add User
          </router-link>
        </div>
        <div class="col-md-6 card py-3 px-2">
          <form @submit.prevent="searchUser()" 
            class="search-input d-flex justify-content-center"
          >
            <input type="text" class="form-control me-1" placeholder="Search user.." v-model="searchInput">
            <button class="btn btn-light-primary" type="submit">Search User</button>
          </form>
        </div>
      </div>
      <div class="col-md-12">
        <!-- Filter Role -->
        <div class="card">
          <div class="card-body py-3">
            <div class="d-flex align-items-center flex-wrap gap-2">
              <!-- All Users -->
              <a class="filter_link"
                :class="isActive == 'all' ? 'active' : '' "
                href="javascript:void(0)"
                @click="filterItem('all')"
              >
                <p class="mb-0">
                  <span class="filter_title">All</span>
                  <span class="filter_count ms-1">({{ users.list.length }})</span>
                </p>
              </a>
              
              <!-- Dynamic Role Filters -->
              <a v-for="role in availableRoles" 
                 :key="role.key"
                 class="filter_link"
                 :class="isActive == role.key ? 'active' : '' "
                 href="javascript:void(0)"
                 @click="filterItem(role.key)"
              >
                <p class="mb-0">
                  <span class="filter_title">{{ role.name }}</span>
                  <span class="filter_count ms-1">({{ role.count }})</span>
                </p>
              </a>
            </div>
          </div>
        </div>
        
        <!-- List User -->
        <div class="card">
          <div class="card-body">
            <div class="table-responsive">
              <table class="table user-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="users.list.length !== 0">
                    <tr v-for="(user, ind) in users.list" :key="user.id">
                      <td>{{ user.email }}</td>
                      <td>{{ user.name }}</td>
                      <td>{{ humanizeText(user.role) }}</td>
                      <td>
                        <button class="btn btn-light-primary icon-btn me-2" 
                          @click="viewUser(user.id)"
                        >
                          <vue-feather type="eye" size="1rem" />
                        </button>
                        <button class="btn btn-light-danger icon-btn me-2"
                          @click="deleteUser(user.id, ind)"
                        >
                          <vue-feather type="trash" size="1rem" />
                        </button>
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr>
                      <td colspan="4" class="text-center">
                        No user match with the query
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>